import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if email is in URL (redirected from WordPress)
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');
    
    if (emailParam) {
      // Auto-login with email from URL
      sessionStorage.setItem('serenity_user_id', emailParam);
      navigate(`/chat?email=${encodeURIComponent(emailParam)}`);
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    
    try {
      const userEmail = email.trim().toLowerCase();
      
      // Store email in sessionStorage
      sessionStorage.setItem('serenity_user_id', userEmail);
      
      toast.success(`Welcome! Starting your journey...`);
      
      // Navigate to chat with email in URL
      setTimeout(() => {
        navigate(`/chat?email=${encodeURIComponent(userEmail)}`);
      }, 500);
    } catch (error) {
      toast.error('Login failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6 bg-white"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="w-full max-w-md"
      >
        <div className="bg-serenity-light-blue border border-serenity-border shadow-lg rounded-3xl p-12">
          <div className="flex flex-col items-center text-center space-y-6">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="w-20 h-20 rounded-full overflow-hidden shadow-lg border-2 border-serenity-primary-blue"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_mentor-chat-3/artifacts/5i6s80b4_Gemini_Generated_Image_13pb5e13pb5e13pb-2.png" 
                alt="Serenity Avatar"
                className="w-full h-full object-cover"
              />
            </motion.div>
            
            <div>
              <h1 className="font-playfair text-4xl text-serenity-primary-blue mb-3 font-semibold">
                Serenity
              </h1>
              <p className="text-serenity-secondary leading-relaxed text-sm">
                Your Personal Pathway Companion, equipping you to identify and strategically leverage your unique gifts and talents for personal and professional success.
              </p>
            </div>

            <form onSubmit={handleLogin} className="w-full space-y-6 pt-4">
              <div className="space-y-2 text-left">
                <label className="text-sm font-manrope font-semibold text-serenity-primary-blue">
                  Your Email Address
                </label>
                <Input
                  data-testid="login-email-input"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 bg-white border-serenity-border focus:ring-2 focus:ring-serenity-accent-blue focus:border-serenity-accent-blue"
                />
                <p className="text-xs text-serenity-muted">
                  Your email is used to save your progress. You can return anytime to continue.
                </p>
              </div>

              <Button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-serenity-primary-blue text-white hover:bg-[#1a3fb3] rounded-full transition-all duration-300 shadow-md hover:shadow-lg font-semibold"
              >
                {loading ? 'Starting your journey...' : 'Begin Your Journey'}
              </Button>
            </form>
          </div>
        </div>

        <p className="text-center text-serenity-muted text-sm mt-6">
          A safe space for self-discovery and career clarity.
          <br />
          <span className="text-xs">Your personal info will not be shared with third parties.</span>
        </p>
      </motion.div>
    </div>
  );
}